/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Alert,
  Cell,
  ExternalStyles,
  FormControl,
  Grid,
  Heading,
  HFlow,
  InfoLabel,
  Link,
  Paper,
  Text,
  Theme,
  useTheme,
  VFlow,
} from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import { DecimalField, ErrorField, NumberField, RadioField } from 'components/form'
import { resolveValue, usePecField } from 'components/form/final-form/hooks/useField'
import { add, isAfter, parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalQuery, useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { TipoGlicemia } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import preNatalPesoGestacionalImage from 'images/atendimento/acompanhamentos/pre-natal/pre-natal-peso-gestacional.png'
import moment from 'moment'
import { useMemo } from 'react'
import { useFormState } from 'react-final-form'
import { useHistory, useRouteMatch } from 'react-router'
import { tipoGlicemiaRecord } from 'types/enums'
import { calcularIMC, calcularRiscoCardiovascular, estadoNutricional } from 'util/atendimento'
import { formatDate } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'
import { formatNumber, parseNumber } from 'util/number'
import { isEmpty, isValid } from 'util/validation/Util'
import { meta as soapMeta } from 'view/atendimentos/atendimento-individual/model'
import {
  convertMedicaoAnterior,
  convertMedicaoAtendimentoAtual,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { categoriaImcReferenciaRecord } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/ganho-peso/model-graficoGanhoPeso'
import { getCategoriaImcReferencia } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/ganho-peso/util-graficoGanhoPeso'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatal,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { usePeriodoGestacional } from 'view/atendimentos/detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { getDumAtual } from 'view/atendimentos/detail/soap/aside/pre-natal/util/util-acompanhamentoPreNatal'
import { getSoapFieldLabelGroup } from 'view/atendimentos/detail/soap/fields-summary-modal/getSoapFieldLabelGroup'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

export interface MedicoesPanelModel {
  openedAccordions?: (string | number)[]
  peso?: string
  altura?: string
  perimetroCefalico?: string
  circunferenciaAbdominal?: string
  perimetroPanturrilha?: string
  pressaoArterialSistolica?: string
  pressaoArterialDiastolica?: string
  frequenciaRespiratoria?: string
  frequenciaCardiaca?: string
  temperatura?: string
  saturacaoO2?: string
  glicemia?: string
  tipoGlicemia?: TipoGlicemia
}

export interface MedicoesPanelProps {
  name: MetaPath<MedicoesPanelModel>
  cidadao: CidadaoAtendimento
  gestacoes: PeriodoGestacaoModel[]
  isGestante?: boolean
  dataAtendimento: Date
  isEscutaInicial?: boolean
  prontuarioId?: ID
  tipoPreNatal?: TipoPreNatal
  style?: ExternalStyles
}

export function MedicoesPanel(props: MedicoesPanelProps) {
  const {
    name,
    cidadao,
    gestacoes,
    dataAtendimento,
    isGestante = false,
    isEscutaInicial = false,
    prontuarioId,
    tipoPreNatal,
    style = css`
      margin: 0;
    `,
  } = props

  const { values: formValues, errors: formErrors } = useFormState({ subscription: { values: true, errors: true } })

  const history = useHistory()
  const match = useRouteMatch()
  const modalUrl = `${match.url}/historicoMedicoes`

  const handleClickGanhoPeso = () => {
    history.push(modalUrl)
  }

  const { getServerTimeNow } = useServerTime()
  const hoje = getServerTimeNow()

  const values = resolveValue(formValues, name) || {}
  const errors = resolveValue(formErrors, name) || {}

  const theme = useTheme()
  const styles = createStyles(theme)

  const alertMessageAbdominal = useMemo(
    () => calcularRiscoCardiovascular(cidadao.sexo, parseNumber(values.circunferenciaAbdominal)),
    [cidadao.sexo, values.circunferenciaAbdominal]
  )

  const imc = useMemo(
    () =>
      isValid(errors.peso) &&
      isValid(errors.altura) &&
      calcularIMC(parseNumber(values.peso), parseNumber(values.altura)),
    [values.peso, values.altura, errors.peso, errors.altura]
  )

  const medicoesAtendimentoAtualAntropometria = useMemo<MedicoesPanelModel | undefined>(
    () =>
      isValid(errors.peso) &&
      isValid(errors.altura) && {
        peso: values.peso,
        altura: values.altura,
      },
    [values.peso, values.altura, errors.peso, errors.altura]
  )

  const idadeDuranteMedicao = moment
    .duration(moment(dataAtendimento).diff(cidadao.dataNascimento, 'days'), 'days')
    .asYears()

  const calcularImc = imc && !isGestante

  const msgEstadoNutricional = useMemo(
    () =>
      calcularImc &&
      estadoNutricional(imc, cidadao.sexo, idadeDuranteMedicao, gestacoes, cidadao.identidadeGeneroDbEnum),
    [calcularImc, imc, cidadao.sexo, cidadao.identidadeGeneroDbEnum, idadeDuranteMedicao, gestacoes]
  )

  const {
    label,
    fields: {
      altura,
      circunferenciaAbdominal,
      frequenciaCardiaca,
      frequenciaRespiratoria,
      glicemia,
      perimetroCefalico,
      perimetroPanturrilha,
      peso,
      saturacaoO2,
      temperatura,
      tipoGlicemia,
    },
  } = getSoapFieldLabelGroup(soapMeta.objetivo.medicoes)

  const {
    input: { value: dumAtendimentoAtual },
  } = usePecField({ name: soapMeta.objetivo.dum, subscription: { value: true } })

  const {
    input: { value: medicoesAnterioresAtendimentoAtual },
  } = usePecField({ name: soapMeta.medicoesAnteriores, subscription: { value: true } })

  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAcompanhamentoPreNatal,
  } = useAcompanhamentoPreNatalQuery({
    variables: { input: prontuarioId },
    skip: !prontuarioId,
  })

  const dumPersistida = useMemo(() => getDumAtual(atendimentosProfUltimaGestacao), [atendimentosProfUltimaGestacao])

  const dumAtendimentoAtualParsed = dumAtendimentoAtual ? parseISO(dumAtendimentoAtual) : undefined

  const { dataInicioGestacao } = usePeriodoGestacional({
    prontuarioId,
    dum: dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    skip: loadingAcompanhamentoPreNatal,
  })

  const { data: medicoesAnteriores } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    skip: !prontuarioId,
  })

  const historicoMedicoes: HistoricoMedicaoModel[] = useMemo(
    () => [
      ...[...(medicoesAnteriores?.medicoes ?? [])]
        .sort((a, b) => b.dataMedicao - a.dataMedicao)
        .map(convertMedicaoOutrosAtendimentos),

      ...(medicoesAtendimentoAtualAntropometria
        ? [convertMedicaoAtendimentoAtual(medicoesAtendimentoAtualAntropometria, dataAtendimento.getTime())]
        : []),

      ...[...(medicoesAnterioresAtendimentoAtual ?? [])].sort((a, b) => b.data - a.data).map(convertMedicaoAnterior),

      ...(medicoesAnteriores?.medicoesNoNascimento && cidadao.dataNascimento != null
        ? [convertMedicoesNoNascimento(medicoesAnteriores.medicoesNoNascimento, cidadao.dataNascimento)]
        : []),
    ],
    [
      medicoesAnteriores,
      medicoesAnterioresAtendimentoAtual,
      dataAtendimento,
      cidadao.dataNascimento,
      medicoesAtendimentoAtualAntropometria,
    ]
  )

  const medicoesAntropometricasPreNatal = useMemo(
    () => getMedicoesAntropometricasPreNatal(historicoMedicoes, tipoPreNatal),
    [historicoMedicoes, tipoPreNatal]
  )

  const medicaoForImcReferencia = useMemo(() => {
    const dataValida = dataInicioGestacao instanceof Date && !isNaN(dataInicioGestacao.getTime())
    const medicoesValidas = Array.isArray(medicoesAntropometricasPreNatal) && !medicoesAntropometricasPreNatal.isEmpty()

    return dataValida && medicoesValidas
      ? getMedicaoForImcReferencia(dataInicioGestacao, medicoesAntropometricasPreNatal)
      : undefined
  }, [dataInicioGestacao, medicoesAntropometricasPreNatal])

  const imcReferencia = useMemo(() => {
    if (!medicaoForImcReferencia) return undefined

    const { peso, altura, dataMedicao } = medicaoForImcReferencia
    if (peso != null && altura != null && dataMedicao != null) {
      const imcReferencia = calcularIMC(peso, altura)
      const categoriaReferencia = getCategoriaImcReferencia(imcReferencia)
      return {
        imc: imcReferencia,
        dataMedicao: new Date(dataMedicao).toISOString(),
        categoriaReferencia: categoriaReferencia,
      }
    }

    return undefined
  }, [medicaoForImcReferencia])

  const categoriaImcReferencia = getCategoriaImcReferencia(imcReferencia?.imc)
  const alertType = categoriaImcReferenciaRecord[categoriaImcReferencia].alertType

  const dumAvaliada = dumAtendimentoAtual || dumPersistida

  const prazoPesoPreGestacional = dumAvaliada && isAfter(hoje, add(dumAvaliada, { weeks: 8 }))

  return (
    <FormSectionAccordion name={name.openedAccordions} title={label} uuid='medicoes' style={style}>
      <div style={{ padding: '1rem' }}>
        <Grid alignItems='flex-start'>
          <Cell size={7}>
            <Paper style={{ padding: '0.625rem' }}>
              <Grid gap={1} alignItems='flex-end' justifyContent='flex-start'>
                <Cell xs={12} lg={8} flexGrow={1}>
                  <VFlow>
                    <HFlow>
                      <DecimalField name={name.peso} label={peso.label} maxLength={7} precisao={3} />
                      <DecimalField name={name.altura} label={altura.label} maxLength={5} precisao={1} />
                      <VFlow>
                        {isGestante ? (
                          <Paper style={styles.paper} data-testid='Paper.ImcPreGestacional'>
                            <VFlow vSpacing={0.25}>
                              <HFlow hSpacing={0.5}>
                                <Heading level={5} style={{ whiteSpace: 'nowrap' }}>
                                  IMC pré-gestacional:
                                </Heading>
                                {!!imcReferencia?.imc && <Text component='span'>{imcReferencia.imc} kg/m²</Text>}
                              </HFlow>
                              {imcReferencia?.categoriaReferencia && (
                                <Alert
                                  inline
                                  type={alertType}
                                  styles={{
                                    wrapper: [styles.alertIcon, styles.alertImcWrapper],
                                  }}
                                >
                                  {imcReferencia.categoriaReferencia}
                                </Alert>
                              )}
                              <Text component='span' fontStyle='italic'>
                                {imcReferencia?.dataMedicao
                                  ? `Registrado em: ${formatDate(imcReferencia?.dataMedicao)}`
                                  : 'Não registrado'}
                              </Text>
                            </VFlow>
                          </Paper>
                        ) : (
                          <InfoLabel title='IMC'>
                            <Text
                              component='span'
                              fontSize={1.5}
                              fontWeight='bold'
                              fontStyle='normal'
                              data-testid='Text.IMC'
                            >
                              {calcularImc ? formatNumber(imc, 2) : '-'}
                            </Text>
                          </InfoLabel>
                        )}
                      </VFlow>
                    </HFlow>
                    {prazoPesoPreGestacional && isGestante && !imcReferencia && (
                      <Alert
                        type='danger'
                        styles={{
                          wrapper: styles.imcPreGestacionalAlertIcon,
                        }}
                      >
                        <Text component='p' fontWeight='normal' color='danger'>
                          <Text component='span' fontWeight='bold' color='danger'>
                            Cálculo do IMC pré-gestacional fora do período de referência.{' '}
                          </Text>
                          O IMC pré-gestacional requer um registro de peso e altura até dois meses antes da gestação ou
                          até a 8ª semana de gestação.
                        </Text>
                      </Alert>
                    )}
                    {isGestante && (
                      <ContextualHelpCard
                        title='Monitoramento de peso na gestação'
                        dataTestId='ContextualHelpCard.MonitoramentoPesoGestacao'
                        description={
                          <Text>
                            Na gestação, o diagnóstico nutricional é baseado no IMC pré-gestacional e no{' '}
                            {!isEscutaInicial ? (
                              <Link onClick={handleClickGanhoPeso} style={{ fontWeight: 'normal' }}>
                                Gráfico de Ganho de Peso.
                              </Link>
                            ) : (
                              'Gráfico de Ganho de Peso.'
                            )}
                            <br />
                            O cálculo do IMC pré-gestacional requer um registro de peso e altura feito até 2 meses antes
                            da gestação ou até a 8ª semana de gestação.
                            <br />
                            Novos cálculos de IMC não são realizados no período gestacional.
                          </Text>
                        }
                        image={preNatalPesoGestacionalImage}
                        imageHeight={80}
                        maxLines={isEscutaInicial ? 1 : 2}
                      />
                    )}
                  </VFlow>
                </Cell>
                {msgEstadoNutricional && (
                  <Cell xs={12} lg={4}>
                    <Alert inline type={msgEstadoNutricional.alertType}>
                      {msgEstadoNutricional.message}
                    </Alert>
                  </Cell>
                )}
              </Grid>
            </Paper>
          </Cell>

          <Cell
            lg={1}
            style={{
              [theme.breakpoints.down('lg')]: {
                display: 'none',
              },
            }}
          />

          <Cell xs={4} lg={3}>
            <DecimalField name={name.perimetroCefalico} label={perimetroCefalico.label} maxLength={5} precisao={1} />
          </Cell>

          <Cell xs={6} lg={7}>
            <Grid gap={1} alignItems='flex-end'>
              <Cell xs={9} lg={5}>
                <DecimalField
                  name={name.circunferenciaAbdominal}
                  label={circunferenciaAbdominal.label}
                  maxLength={5}
                  precisao={1}
                />
              </Cell>
              {alertMessageAbdominal && (
                <Cell xs={12} lg={5}>
                  <Alert inline type='warning'>
                    {alertMessageAbdominal}
                  </Alert>
                </Cell>
              )}
            </Grid>
          </Cell>

          <Cell size={1} />

          <Cell xs={4} lg={3}>
            <DecimalField
              name={name.perimetroPanturrilha}
              label={perimetroPanturrilha.label}
              maxLength={5}
              precisao={1}
            />
          </Cell>
        </Grid>

        <Grid style={styles.border}>
          <Cell xs={12} lg={4}>
            <FormControl label='Pressão arterial (mmHg)'>
              <HFlow>
                <NumberField name={name.pressaoArterialSistolica} label='' maxLength={3} />
                <Text component='p' fontSize={1.25}>
                  /
                </Text>
                <NumberField name={name.pressaoArterialDiastolica} label='' maxLength={3} />
              </HFlow>
            </FormControl>
          </Cell>

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaRespiratoria} label={frequenciaRespiratoria.label} maxLength={3} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaCardiaca} label={frequenciaCardiaca.label} maxLength={3} />
          </Cell>

          <Cell xs={4} lg={3}>
            <DecimalField name={name.temperatura} label={temperatura.label} maxLength={4} precisao={1} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.saturacaoO2} label={saturacaoO2.label} maxLength={3} />
          </Cell>
        </Grid>

        <Grid style={styles.border}>
          <Cell size={4}>
            <NumberField name={name.glicemia} label={glicemia.label} maxLength={3} />
          </Cell>

          <Cell xs={12} lg={8}>
            <FormControl label={tipoGlicemia.label} required={!isEmpty(values.glicemia)}>
              <VFlow vSpacing={0.5}>
                <HFlow style={{ marginLeft: '-0.25rem' }}>
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.NAO_ESPECIFICADO]}
                    value={TipoGlicemia.NAO_ESPECIFICADO}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.JEJUM]}
                    value={TipoGlicemia.JEJUM}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.PREPRANDIAL]}
                    value={TipoGlicemia.PREPRANDIAL}
                    disabled={!values.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.POSPRANDIAL]}
                    value={TipoGlicemia.POSPRANDIAL}
                    disabled={!values.glicemia}
                  />
                </HFlow>
                <ErrorField name={name.tipoGlicemia} />
              </VFlow>
            </FormControl>
          </Cell>
        </Grid>
      </div>
    </FormSectionAccordion>
  )
}

const createStyles = (theme: Theme) => ({
  border: css`
    border-top: 0.063rem solid ${theme.pallete.gray.c80};
    margin-top: 1rem;
  `,
  imcPreGestacionalAlertIcon: css`
    svg {
      width: 1rem;
      height: 1rem;
    }
  `,

  paper: css`
    border: 0;
    background: ${theme.pallete.surface.background};
    padding: 0.5rem;
    ${theme.breakpoints.up('lg')} {
      max-height: 5.75rem;
    }
    ${theme.breakpoints.down('lg')} {
      max-height: 6.5rem;
    }
  `,

  alertIcon: css`
    svg {
      width: 1rem;
      height: 1rem;
    }
  `,
  alertImcWrapper: css`
    background-color: transparent;
    border-width: 0;
    padding: 0;
    min-height: 0;
  `,
})
